/**
 * @fileoverview Timesheet Approvals — partner review queue (MOR-906)
 *
 * URL: /dashboard/timesheet-approvals
 * Protection: authenticated. The page and sidebar item are hidden unless
 * `approvalAccess` is true (designated approver, or org admin/owner while
 * none exist).
 *
 * Dependencies:
 * - Convex: timesheets.entries.approvalQueue / approveEntries / rejectEntries
 */

import { useEffect } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { convexQueryOrIdle } from "@/lib/convex-query-idle";

import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";

import { useActiveOrg } from "@/utils/org-context";
import { ApprovalsQueue } from "@/components/timesheets/ApprovalsQueue";

export const Route = createFileRoute(
  "/_app/_auth/dashboard/_layout/timesheet-approvals",
)({
  beforeLoad: () => ({ headerTitle: "Timesheet Approvals" }),
  component: TimesheetApprovalsPage,
});

function TimesheetApprovalsPage() {
  const { orgId } = useActiveOrg();
  const navigate = useNavigate();

  const { data: settings } = useQuery({
    ...convexQueryOrIdle(
      api.timesheets.settings.getForOrg,
      orgId ? { orgId } : undefined,
    ),
    enabled: !!orgId,
  });
  // Approver model (MOR-906): designated approvers from Timesheet Settings,
  // falling back to org admins/owners while none are designated.
  const { data: canApprove } = useQuery({
    ...convexQueryOrIdle(
      api.timesheets.entries.approvalAccess,
      orgId ? { orgId } : undefined,
    ),
    enabled: !!orgId,
  });

  useEffect(() => {
    if (canApprove === false) {
      void navigate({ to: "/dashboard/timesheets" });
    }
  }, [canApprove, navigate]);

  if (orgId && canApprove !== true) {
    return null;
  }

  return (
    <div className="flex flex-1 flex-col gap-5 bg-muted p-6">
      {!orgId ? (
        <div className="text-sm text-muted-foreground">
          Select an organisation to review timesheets.
        </div>
      ) : (
        <ApprovalsQueue
          orgId={orgId as Id<"organizations">}
          unitMinutes={settings?.unitMinutes ?? 6}
          roundingMode={settings?.roundingMode ?? "up"}
        />
      )}
    </div>
  );
}
